import { Link } from 'react-router-dom';
import {
  X, Check, ChevronRight,
  Landmark, Network, BookUser, Upload, UserPlus, ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import type { FirstRunStep } from '../lib/firstRun';

/** Pillar icon per step, keyed to firstRunSteps() keys. */
const STEP_ICON: Record<string, LucideIcon> = {
  'connect': Landmark,
  'entity': Network,
  'contact': BookUser,
  'document': Upload,
  'invite': UserPlus,
  // steward-app#1343 step 8a. The same icon People → Advisors puts on a connected firm
  // row, so the checklist row and the page it links to read as one thing. The lookup is
  // `Icon && ...`-guarded, so a missing entry degrades to no icon rather than a crash; this
  // is here so the row does not sit iconless beside five that have one.
  'advisor': ShieldCheck,
};

/**
 * First-run "getting started" checklist (FR1). A dismissible, editorial card on
 * the Dashboard — mirrors the NameNudge card treatment (bronze-bordered, calm,
 * no confetti). Completion is data-derived (see firstRunSteps); rows only
 * deep-link, they never mark themselves done.
 *
 * The completed state keeps the full list, struck through, rather than collapsing
 * to a one-line "all set". A plain load hides the card entirely once complete
 * (see shouldShowFirstRun), so the only way to reach this state is reopening from
 * the account menu — and someone who asks to see the list means the list.
 */
export function GettingStartedCard({
  steps,
  done,
  total,
  isComplete,
  onDismiss,
}: {
  steps: FirstRunStep[];
  done: number;
  total: number;
  isComplete: boolean;
  onDismiss: () => void;
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div data-testid="getting-started" data-tour="getting-started" className="relative mb-8 rounded-token border border-bronze/30 bg-surface p-5 shadow-panel">
      <button
        onClick={onDismiss}
        aria-label="Dismiss getting started"
        className="absolute right-3 top-3 text-slate hover:text-navy bg-transparent border-none cursor-pointer p-1"
      >
        <X size={16} strokeWidth={1.75} aria-hidden />
      </button>

      {isComplete ? (
        <div className="flex items-center gap-2 pr-6 mb-1">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-navy text-cream">
            <Check size={12} strokeWidth={2.5} aria-hidden />
          </span>
          <h2 className="font-serif text-[17px] font-normal m-0 text-navy">You're all set</h2>
        </div>
      ) : (
        <h2 className="font-serif text-[17px] font-normal m-0 mb-1 text-navy">Getting started</h2>
      )}
      <p className="text-[13px] text-slate mb-4 max-w-md">
        {isComplete ? 'Every step here is done.' : 'A few steps to make your workspace useful.'}
      </p>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold text-slate uppercase tracking-[0.12em] font-sans">
            {done} of {total} done
          </span>
        </div>
        <div className="h-1 w-full rounded-token bg-navy/8 overflow-hidden">
          <div className="h-full bg-navy transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps */}
      <ul className="list-none m-0 p-0 flex flex-col">
        {steps.map(step => {
          const Icon = STEP_ICON[step.key];
          return (
            <li key={step.key}>
              <Link
                to={step.href}
                className="group flex items-center gap-3 py-2 no-underline border-b border-line/60 last:border-b-0"
              >
                <span
                  className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border ${
                    step.done ? 'bg-navy border-navy text-cream' : 'border-line-strong text-transparent'
                  }`}
                  aria-hidden
                >
                  <Check size={11} strokeWidth={2.5} />
                </span>
                {Icon && (
                  <Icon size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-slate" />
                )}
                <span className={`flex-1 text-[13px] font-sans ${step.done ? 'text-slate line-through' : 'text-navy'}`}>
                  {step.label}
                </span>
                {!step.done && (
                  <ChevronRight
                    size={15}
                    strokeWidth={1.75}
                    aria-hidden
                    className="shrink-0 text-slate opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
